import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as R from 'ramda'

import { useControlledForm, useFormApiError, useFormReset } from 'hooks'
import {
  MESSAGE_FIELD,
  SENDER_FIELD,
  SUBJECT_FIELD,
  getDefaultValues,
  getVariables,
} from './model'
import { getSchema } from './schema'

export const useMessageTemplateManagementModalForm = (state, handlers) => {
  const { template, isOpen } = state
  const { onClose, onConfirm } = handlers

  const [apiError, setApiError] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)

  const defaultValues = getDefaultValues()
  const schema = getSchema()

  const variables = getVariables(template?.language)

  const internalState = React.useMemo(
    () => ({
      [SENDER_FIELD]: template?.sender_name,
      [SUBJECT_FIELD]: template?.subject,
      [MESSAGE_FIELD]: template?.body,
    }),
    [template],
  )

  const handleFormData = React.useCallback(
    data => {
      onConfirm(
        {
          body: data?.message,
          sender_name: data?.sender,
          subject: data?.subject,
        },
        {
          onFailureAction: setApiError,
          postSuccessAction: () => {
            setShouldReset(true)
            onClose()
          },
        },
      )
    },
    [onClose, onConfirm],
  )

  const {
    control,
    formState,
    handleFormApiError,
    handleFormSubmit,
    handleFormReset,
    isDirtyOrInvalid,
  } = useControlledForm({
    values: template ? internalState : defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  useFormApiError({ apiError }, handleFormApiError)
  useFormReset(
    { shouldReset: R.and(formState.isSubmitSuccessful, shouldReset) },
    handleFormReset,
  )

  React.useEffect(() => {
    if (isOpen) {
      setShouldReset(false)
    }
  }, [isOpen])

  return {
    state: { control, formState, variables, isDirtyOrInvalid },
    handlers: { handleFormSubmit, handleFormReset },
  }
}
